/**
 * ConversationsBrowser.js
 *
 * Shared "All Conversations" panel used by BOTH agent surfaces:
 *   - AgentPortal (the standalone /agent route)
 *   - AgentHandoffDashboard (embedded in the Chatbot Workflow Designer)
 *
 * One source of truth so the two surfaces can't drift. It lists every
 * conversation for a workflow (bot- or customer-initiated) from the durable
 * chatbot_conversations record — with status / initiator / date-range / mobile
 * search filters — and lets an agent deliberately JUMP IN (interjection) on a
 * bot-driven conversation: from that point the bot workflow is bypassed and the
 * agent chats directly over the customer's real deploy channel.
 *
 * Props:
 *   - workflowId : the workflow to list conversations for
 *   - agentName  : the agent doing the interjection (stamped on the record)
 *   - onOpen     : (handoffLike) => void — called to open the chat panel. Receives
 *                  a { user_id, channel, priority, department, reason, mode,
 *                  message_history } object compatible with either surface's chat panel.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCw, Clock, CheckCircle2, User, Bot,
  MessageSquare, LogIn, Headphones,
} from 'lucide-react';
import { apiCall, apiCallJson } from './Api';

const STATUS_META = {
  bot:            { label: 'Bot',      cls: 'bg-blue-100 text-blue-700',     icon: Bot },
  handoff_auto:   { label: 'Auto',     cls: 'bg-amber-100 text-amber-700',   icon: Headphones },
  handoff_manual: { label: 'Agent',    cls: 'bg-purple-100 text-purple-700', icon: User },
  resolved:       { label: 'Resolved', cls: 'bg-green-100 text-green-700',    icon: CheckCircle2 },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.bot;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
};

// Strip provider/routing suffixes for DISPLAY only (the raw channel value is still
// used for API calls). e.g. whatsapp_interakt -> whatsapp, web_channel -> web.
const prettyChannel = (ch) => (ch || 'web')
  .replace(/_via_interakt$/i, '')
  .replace(/_interakt$/i, '')
  .replace(/_channel$/i, '');

const ChannelBadge = ({ channel }) => {
  const base = prettyChannel(channel);
  const labels = { web: 'WEB', whatsapp: 'WA', telegram: 'TG', facebook: 'FB', instagram: 'IG' };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full text-[10px] text-gray-600 font-medium">
      {labels[base] || base?.toUpperCase()?.slice(0, 3) || 'CH'} {base || 'web'}
    </span>
  );
};

const ConversationsBrowser = ({ workflowId, agentName, onOpen }) => {
  const [conversations, setConversations] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [initiatorFilter, setInitiatorFilter] = useState('');
  const [afterDate, setAfterDate] = useState('');
  const [beforeDate, setBeforeDate] = useState('');
  const [interjecting, setInterjecting] = useState(null);

  const load = useCallback(async () => {
    if (!workflowId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: '150' });
      if (statusFilter) qs.set('status', statusFilter);
      if (initiatorFilter) qs.set('initiator', initiatorFilter);
      if (search) qs.set('search', search.trim());
      if (afterDate) qs.set('after', afterDate);
      if (beforeDate) qs.set('before', beforeDate);
      const data = await apiCallJson(`/api/chatbot/workflows/${workflowId}/conversations?${qs.toString()}`);
      setConversations(data.conversations || []);
      setCounts(data.counts || {});
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
    setLoading(false);
  }, [workflowId, statusFilter, initiatorFilter, search, afterDate, beforeDate]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  const jumpIn = async (conv) => {
    setInterjecting(conv.session_id);
    try {
      const resp = await apiCall(`/api/chatbot/workflows/${workflowId}/conversations/interject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: conv.user_id,
          channel: conv.channel,
          agent_id: agentName,
          reason: 'Agent interjection',
        }),
      });
      if (resp.ok) {
        onOpen({
          user_id: conv.user_id,
          channel: conv.channel,
          priority: conv.priority || 'normal',
          department: conv.department || '',
          reason: conv.reason || '',
          mode: 'manual',
          message_history: [],
        });
      } else {
        alert('Could not jump into this conversation.');
      }
    } catch (err) {
      console.error('Interject failed:', err);
      alert(`Jump-in failed: ${err.message || err}`);
    }
    setInterjecting(null);
  };

  const StatusChip = ({ value, label }) => (
    <button
      onClick={() => setStatusFilter(statusFilter === value ? '' : value)}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        statusFilter === value
          ? 'bg-gray-800 text-white border-gray-800'
          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
      }`}
    >
      {label}{counts[value] != null ? ` (${counts[value]})` : ''}
    </button>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 space-y-2 flex-shrink-0">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by mobile number or user ID..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <select value={initiatorFilter} onChange={(e) => setInitiatorFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">Any initiator</option>
            <option value="customer">Customer-initiated</option>
            <option value="bot">Bot-initiated</option>
          </select>
          <button onClick={load} className="p-2 hover:bg-gray-100 rounded-lg text-purple-600">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> Active between
          </span>
          <input type="date" value={afterDate} max={beforeDate || undefined}
            onChange={(e) => setAfterDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
          <span className="text-xs text-gray-400">and</span>
          <input type="date" value={beforeDate} min={afterDate || undefined}
            onChange={(e) => setBeforeDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
          {(afterDate || beforeDate) && (
            <button onClick={() => { setAfterDate(''); setBeforeDate(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline">
              clear dates
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <StatusChip value="" label="All" />
          <StatusChip value="bot" label="Bot" />
          <StatusChip value="handoff_auto" label="Auto handoff" />
          <StatusChip value="handoff_manual" label="Agent" />
          <StatusChip value="resolved" label="Resolved" />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="h-12 w-12 mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 text-sm font-medium">No conversations</p>
            <p className="text-gray-400 text-xs mt-1">Conversations appear here as customers and campaigns start them</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {conversations.map((conv) => {
              const canJumpIn = conv.status === 'bot' || conv.status === 'resolved';
              const isLive = conv.status === 'handoff_auto' || conv.status === 'handoff_manual';
              const busy = interjecting === conv.session_id;
              return (
                <div key={conv.session_id} className="px-4 py-3.5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-sm text-gray-800 truncate">{conv.user_id}</span>
                        <ChannelBadge channel={conv.channel} />
                        <StatusBadge status={conv.status} />
                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                          {conv.initiator === 'bot'
                            ? <><Bot className="h-3 w-3" /> bot-started</>
                            : <><User className="h-3 w-3" /> customer-started</>}
                        </span>
                      </div>
                      {conv.last_message_preview && (
                        <div className="text-xs text-gray-500 line-clamp-1">
                          <span className="font-medium text-gray-400">
                            {conv.last_message_role === 'user' ? 'Customer' :
                             conv.last_message_role === 'agent' ? 'Agent' : 'Bot'}:
                          </span> {conv.last_message_preview}
                        </div>
                      )}
                      <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-3">
                        <span>{conv.message_count} msg{conv.message_count === 1 ? '' : 's'}</span>
                        {conv.agent_id && <span className="text-purple-600">agent: {conv.agent_id}</span>}
                        {conv.last_activity_at && (
                          <span>{new Date(conv.last_activity_at).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                      {isLive ? (
                        <button
                          onClick={() => onOpen({
                            user_id: conv.user_id, channel: conv.channel,
                            priority: conv.priority || 'normal', department: conv.department || '',
                            reason: conv.reason || '', mode: conv.handoff_mode || 'manual',
                            message_history: [],
                          })}
                          className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700 flex items-center gap-1.5">
                          <MessageSquare className="h-3.5 w-3.5" /> Open
                        </button>
                      ) : (
                        <button
                          onClick={() => jumpIn(conv)}
                          disabled={busy || !canJumpIn}
                          title="Take over this conversation — the bot flow is bypassed from here"
                          className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1.5">
                          <LogIn className="h-3.5 w-3.5" /> {busy ? 'Joining…' : 'Jump In'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsBrowser;
